import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Servicio para manejar operaciones de Declaración de Renta
 * Diseñado para ser usado por agentes autónomos vía MCP
 *
 * Este servicio encapsula todas las llamadas al API relacionadas con
 * declaraciones de renta y declaraciones juradas.
 */
class DeclaracionRentaService {
    /**
     * Configurar headers de autenticación
     */
    _getHeaders(token) {
        return {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    }

    /**
     * Manejar errores de API
     */
    _handleError(error, defaultMessage) {
        if (error.response) {
            throw new Error(error.response.data.message || defaultMessage);
        } else if (error.request) {
            throw new Error('No se pudo conectar con el servidor');
        } else {
            throw new Error(error.message || defaultMessage);
        }
    }

    /**
     * Obtener declaraciones juradas para un año específico
     *
     * @param {number} year - Año de la declaración
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Array>} Lista de declaraciones juradas
     */
    async getDeclaracionesJuradas(year, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/juradas`,
                {
                    params: { year, id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data.data || [];
        } catch (error) {
            this._handleError(error, 'Error al obtener declaraciones juradas');
        }
    }

    /**
     * Obtener declaraciones de renta para un año específico
     *
     * @param {number} year - Año de la declaración
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Array>} Lista de declaraciones de renta
     */
    async getDeclaracionesRenta(year, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/renta`,
                {
                    params: { year, id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data.data || [];
        } catch (error) {
            this._handleError(error, 'Error al obtener declaraciones de renta');
        }
    }

    /**
     * Crear nueva declaración jurada
     *
     * @param {Object} data - Datos de la declaración jurada
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Declaración jurada creada
     */
    async createDeclaracionJurada(data, token) {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/declaracion-renta/juradas`,
                data,
                { headers: this._getHeaders(token) }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al crear declaración jurada');
        }
    }

    /**
     * Crear nueva declaración de renta
     *
     * @param {Object} data - Datos de la declaración de renta
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Declaración de renta creada
     */
    async createDeclaracionRenta(data, token) {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/declaracion-renta/renta`,
                data,
                { headers: this._getHeaders(token) }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al crear declaración de renta');
        }
    }

    /**
     * Actualizar declaración jurada existente
     *
     * @param {number} id - ID de la declaración jurada
     * @param {Object} data - Datos actualizados
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Declaración jurada actualizada
     */
    async updateDeclaracionJurada(id, data, token) {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/api/declaracion-renta/juradas/${id}`,
                data,
                { headers: this._getHeaders(token) }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al actualizar declaración jurada');
        }
    }

    /**
     * Actualizar declaración de renta existente
     *
     * @param {number} id - ID de la declaración de renta
     * @param {Object} data - Datos actualizados
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Declaración de renta actualizada
     */
    async updateDeclaracionRenta(id, data, token) {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/api/declaracion-renta/renta/${id}`,
                data,
                { headers: this._getHeaders(token) }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al actualizar declaración de renta');
        }
    }

    /**
     * Eliminar declaración jurada
     *
     * @param {number} id - ID de la declaración jurada
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Resultado de la eliminación
     */
    async deleteDeclaracionJurada(id, id_company, token) {
        try {
            const response = await axios.delete(
                `${API_BASE_URL}/api/declaracion-renta/juradas/${id}`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al eliminar declaración jurada');
        }
    }

    /**
     * Eliminar declaración de renta
     *
     * @param {number} id - ID de la declaración de renta
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Resultado de la eliminación
     */
    async deleteDeclaracionRenta(id, token) {
        try {
            const response = await axios.delete(
                `${API_BASE_URL}/api/declaracion-renta/renta/${id}`,
                { headers: this._getHeaders(token) }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al eliminar declaración de renta');
        }
    }

    /**
     * Descargar PDF de declaración jurada
     *
     * @param {number} id - ID de la declaración jurada
     * @param {string} token - Token de autenticación
     * @returns {Promise<Blob>} PDF generado
     */
    async downloadDeclaracionJuradaPDF(id, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/juradas/${id}/pdf`,
                {
                    headers: this._getHeaders(token),
                    responseType: 'blob'
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al descargar PDF');
        }
    }

    /**
     * Obtener tipos de declaraciones juradas disponibles
     *
     * @param {string} token - Token de autenticación
     * @returns {Promise<Array>} Lista de tipos disponibles
     */
    async getTiposDeclaracionJurada(token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/juradas/tipos`,
                { headers: this._getHeaders(token) }
            );
            return response.data.data || [];
        } catch (error) {
            this._handleError(error, 'Error al obtener tipos de declaración jurada');
        }
    }

    // ========== MÉTODOS ESPECÍFICOS PARA DJ1835 - BIENES RAÍCES ARRENDADOS ==========

    /**
     * Obtener registros de bienes raíces arrendados de una declaración
     *
     * @param {number} idDeclaracion - ID de la declaración jurada
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Array>} Lista de bienes raíces arrendados
     */
    async getBienesRaicesArrendados(idDeclaracion, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/dj1835/${idDeclaracion}`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data.data || [];
        } catch (error) {
            this._handleError(error, 'Error al obtener bienes raíces arrendados');
        }
    }

    /**
     * Crear un nuevo registro de bien raíz arrendado
     *
     * @param {number} idDeclaracion - ID de la declaración jurada
     * @param {Object} data - Datos del bien raíz arrendado
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Registro creado
     */
    async createBienRaizArrendado(idDeclaracion, data, id_company, token) {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/declaracion-renta/dj1835/${idDeclaracion}`,
                data,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al crear bien raíz arrendado');
        }
    }

    /**
     * Actualizar un registro de bien raíz arrendado
     *
     * @param {number} id - ID del registro
     * @param {Object} data - Datos actualizados
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Registro actualizado
     */
    async updateBienRaizArrendado(id, data, id_company, token) {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/api/declaracion-renta/dj1835/${id}`,
                data,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al actualizar bien raíz arrendado');
        }
    }

    /**
     * Eliminar un registro de bien raíz arrendado
     *
     * @param {number} id - ID del registro
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Resultado de la eliminación
     */
    async deleteBienRaizArrendado(id, id_company, token) {
        try {
            const response = await axios.delete(
                `${API_BASE_URL}/api/declaracion-renta/dj1835/${id}`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al eliminar bien raíz arrendado');
        }
    }

    /**
     * Generar y descargar archivo CSV
     *
     * @param {number} idDeclaracion - ID de la declaración jurada
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Blob>} Archivo CSV
     */
    async downloadDJ1835CSV(idDeclaracion, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/dj1835/${idDeclaracion}/csv`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token),
                    responseType: 'blob'
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al descargar CSV');
        }
    }

    /**
     * Validar datos antes de generar CSV
     *
     * @param {number} idDeclaracion - ID de la declaración jurada
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Resultado de validación
     */
    async validateDJ1835Data(idDeclaracion, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/dj1835/${idDeclaracion}/validate`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al validar datos');
        }
    }

    /**
     * Helper para descargar el CSV y guardarlo en el navegador
     *
     * @param {number} idDeclaracion - ID de la declaración jurada
     * @param {number} id_company - ID de la empresa
     * @param {string} rutEmpresa - RUT de la empresa (para nombre de archivo)
     * @param {number} year - Año de la declaración (para nombre de archivo)
     * @param {string} token - Token de autenticación
     */
    async downloadAndSaveDJ1835CSV(idDeclaracion, id_company, rutEmpresa, year, token) {
        try {
            const blob = await this.downloadDJ1835CSV(idDeclaracion, id_company, token);

            // Crear URL del blob
            const url = window.URL.createObjectURL(blob);

            // Crear elemento de descarga
            const link = document.createElement('a');
            link.href = url;
            link.download = `DJ1835_${rutEmpresa}_${year}.csv`;

            // Simular click para descargar
            document.body.appendChild(link);
            link.click();

            // Limpiar
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            this._handleError(error, 'Error al descargar y guardar CSV');
        }
    }

    // ========== MÉTODOS ESPECÍFICOS PARA DJ1879 - HONORARIOS Y RETENCIONES ==========

    /**
     * Obtener registros de honorarios de una declaración
     *
     * @param {number} idDeclaracion - ID de la declaración jurada
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Array>} Lista de honorarios
     */
    async getHonorarios(idDeclaracion, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/dj1879/${idDeclaracion}`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data.data || [];
        } catch (error) {
            this._handleError(error, 'Error al obtener honorarios');
        }
    }

    /**
     * Crear un nuevo registro de honorario
     *
     * @param {number} idDeclaracion - ID de la declaración jurada
     * @param {Object} data - Datos del honorario
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Registro creado
     */
    async createHonorario(idDeclaracion, data, id_company, token) {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/declaracion-renta/dj1879/${idDeclaracion}`,
                data,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al crear honorario');
        }
    }

    /**
     * Actualizar un registro de honorario
     *
     * @param {number} id - ID del registro
     * @param {Object} data - Datos actualizados
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Registro actualizado
     */
    async updateHonorario(id, data, id_company, token) {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/api/declaracion-renta/dj1879/${id}`,
                data,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al actualizar honorario');
        }
    }

    /**
     * Eliminar un registro de honorario
     *
     * @param {number} id - ID del registro
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Resultado de la eliminación
     */
    async deleteHonorario(id, id_company, token) {
        try {
            const response = await axios.delete(
                `${API_BASE_URL}/api/declaracion-renta/dj1879/${id}`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al eliminar honorario');
        }
    }

    /**
     * Generar y descargar archivo CSV de DJ1879
     *
     * @param {number} idDeclaracion - ID de la declaración jurada
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Blob>} Archivo CSV
     */
    async downloadDJ1879CSV(idDeclaracion, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/dj1879/${idDeclaracion}/csv`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token),
                    responseType: 'blob'
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al descargar CSV');
        }
    }

    /**
     * Validar datos antes de generar CSV de DJ1879
     *
     * @param {number} idDeclaracion - ID de la declaración jurada
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Resultado de validación
     */
    async validateDJ1879Data(idDeclaracion, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/dj1879/${idDeclaracion}/validate`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al validar datos');
        }
    }

    /**
     * Helper para descargar el CSV de DJ1879 y guardarlo en el navegador
     *
     * @param {number} idDeclaracion - ID de la declaración jurada
     * @param {number} id_company - ID de la empresa
     * @param {string} rutEmpresa - RUT de la empresa (para nombre de archivo)
     * @param {number} year - Año de la declaración (para nombre de archivo)
     * @param {string} token - Token de autenticación
     */
    async downloadAndSaveDJ1879CSV(idDeclaracion, id_company, rutEmpresa, year, token) {
        try {
            const blob = await this.downloadDJ1879CSV(idDeclaracion, id_company, token);

            // Crear URL del blob
            const url = window.URL.createObjectURL(blob);

            // Crear elemento de descarga
            const link = document.createElement('a');
            link.href = url;
            link.download = `DJ1879_${rutEmpresa}_${year}.csv`;

            // Simular click para descargar
            document.body.appendChild(link);
            link.click();

            // Limpiar
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            this._handleError(error, 'Error al descargar y guardar CSV');
        }
    }
    // ========== MÉTODOS ESPECÍFICOS PARA DJ1887 - RENTAS ART. 42 N°1 (SUELDOS) ==========

    /**
     * Obtener registros de remuneraciones de una declaración
     *
     * @param {number} idDeclaracion - ID de la declaración jurada
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Array>} Lista de remuneraciones
     */
    async getRemuneraciones(idDeclaracion, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/dj1887/${idDeclaracion}`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data.data || [];
        } catch (error) {
            this._handleError(error, 'Error al obtener remuneraciones');
        }
    }

    /**
     * Crear un nuevo registro de remuneración
     *
     * @param {number} idDeclaracion - ID de la declaración jurada
     * @param {Object} data - Datos de la remuneración
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Registro creado
     */
    async createRemuneracion(idDeclaracion, data, id_company, token) {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/declaracion-renta/dj1887/${idDeclaracion}`,
                data,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al crear remuneración');
        }
    }

    /**
     * Actualizar un registro de remuneración
     *
     * @param {number} id - ID del registro
     * @param {Object} data - Datos actualizados
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Registro actualizado
     */
    async updateRemuneracion(id, data, id_company, token) {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/api/declaracion-renta/dj1887/${id}`,
                data,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al actualizar remuneración');
        }
    }

    /**
     * Eliminar un registro de remuneración
     *
     * @param {number} id - ID del registro
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Resultado de la eliminación
     */
    async deleteRemuneracion(id, id_company, token) {
        try {
            const response = await axios.delete(
                `${API_BASE_URL}/api/declaracion-renta/dj1887/${id}`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al eliminar remuneración');
        }
    }

    /**
     * Generar y descargar archivo CSV de DJ1887
     *
     * @param {number} idDeclaracion - ID de la declaración jurada
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Blob>} Archivo CSV
     */
    async downloadDJ1887CSV(idDeclaracion, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/dj1887/${idDeclaracion}/csv`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token),
                    responseType: 'blob'
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al descargar CSV');
        }
    }

    /**
     * Validar datos antes de generar CSV de DJ1887
     *
     * @param {number} idDeclaracion - ID de la declaración jurada
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Resultado de validación
     */
    async validateDJ1887Data(idDeclaracion, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/dj1887/${idDeclaracion}/validate`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al validar datos');
        }
    }

    /**
     * Helper para descargar el CSV de DJ1887 y guardarlo en el navegador
     *
     * @param {number} idDeclaracion - ID de la declaración jurada
     * @param {number} id_company - ID de la empresa
     * @param {string} rutEmpresa - RUT de la empresa (para nombre de archivo)
     * @param {number} year - Año de la declaración (para nombre de archivo)
     * @param {string} token - Token de autenticación
     */
    async downloadAndSaveDJ1887CSV(idDeclaracion, id_company, rutEmpresa, year, token) {
        try {
            const blob = await this.downloadDJ1887CSV(idDeclaracion, id_company, token);

            // Crear URL del blob
            const url = window.URL.createObjectURL(blob);

            // Crear elemento de descarga
            const link = document.createElement('a');
            link.href = url;
            link.download = `DJ1887_${rutEmpresa}_${year}.csv`;

            // Simular click para descargar
            document.body.appendChild(link);
            link.click();

            // Limpiar
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            this._handleError(error, 'Error al descargar y guardar CSV');
        }
    }

    /**
     * Generar y descargar PDF de DJ1887
     *
     * @param {number} idDeclaracion - ID de la declaración jurada
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Blob>} Archivo PDF
     */
    async downloadDJ1887PDF(idDeclaracion, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/dj1887/${idDeclaracion}/pdf`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token),
                    responseType: 'blob'
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al descargar PDF');
        }
    }

    /**
     * Helper para descargar el PDF de DJ1887 y guardarlo en el navegador
     *
     * @param {number} idDeclaracion - ID de la declaración jurada
     * @param {number} id_company - ID de la empresa
     * @param {string} rutEmpresa - RUT de la empresa (para nombre de archivo)
     * @param {number} year - Año de la declaración (para nombre de archivo)
     * @param {string} token - Token de autenticación
     */
    async downloadAndSaveDJ1887PDF(idDeclaracion, id_company, rutEmpresa, year, token) {
        try {
            const blob = await this.downloadDJ1887PDF(idDeclaracion, id_company, token);

            // Crear URL del blob
            const url = window.URL.createObjectURL(blob);

            // Crear elemento de descarga
            const link = document.createElement('a');
            link.href = url;
            link.download = `DJ1887_${rutEmpresa}_${year}.pdf`;

            // Simular click para descargar
            document.body.appendChild(link);
            link.click();

            // Limpiar
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            this._handleError(error, 'Error al descargar y guardar PDF');
        }
    }
}


// Exportar instancia única (singleton)
const declaracionRentaService = new DeclaracionRentaService();
export default declaracionRentaService;
